import React, { useEffect, useState, useCallback } from "react";
import { Button, Spinner } from "react-bootstrap";
import OmrService from "../../../_services/OmrService";

interface Props {
  omrTestId: number;
  numberOfSets: number;
  totalQuestions: number;
}

const SET_LABELS = ["A", "B", "C", "D"];

const AnswerKeyTab: React.FC<Props> = ({ omrTestId, numberOfSets, totalQuestions }) => {
  const [keys, setKeys] = useState<Record<string, number[]>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const setLabels = SET_LABELS.slice(0, numberOfSets);

  const loadKeys = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await OmrService.getAnswerKeys(omrTestId);
      const rows = resp.data?.data || [];
      const loaded: Record<string, number[]> = {};
      for (const row of rows) {
        const answers = typeof row.AnswersJson === "string" ? JSON.parse(row.AnswersJson) : (row.AnswersJson || []);
        loaded[row.SetCode] = Array.from({ length: totalQuestions }, (_, i) => {
          const entry = answers.find((a: any) => a.q === i + 1);
          return entry ? entry.answer : 0;
        });
      }
      // Initialize empty sets
      for (const label of setLabels) {
        if (!loaded[label]) {
          loaded[label] = Array(totalQuestions).fill(0);
        }
      }
      setKeys(loaded);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [omrTestId, totalQuestions, setLabels]);

  useEffect(() => {
    loadKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omrTestId]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const sets = setLabels.map((label) => ({
        setCode: label,
        answers: (keys[label] || [])
          .map((ans, i) => ({ q: i + 1, answer: ans }))
          .filter((a) => a.answer > 0),
      }));
      await OmrService.saveAnswerKeys(omrTestId, sets);
      setMessage("Answer keys saved successfully");
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) {
      setMessage("Failed to save: " + (err.message || ""));
    } finally {
      setSaving(false);
    }
  };

  const handleCellChange = (setCode: string, qIdx: number, value: number) => {
    setKeys((prev) => {
      const copy = { ...prev };
      const arr = [...(copy[setCode] || Array(totalQuestions).fill(0))];
      arr[qIdx] = value;
      copy[setCode] = arr;
      return copy;
    });
  };

  if (loading) {
    return <div style={{ textAlign: "center", padding: 20 }}><Spinner animation="border" size="sm" /> Loading...</div>;
  }

  // Display in a grid: questions as rows, sets as column groups
  const questionsPerPage = 30;
  const pages = Math.ceil(totalQuestions / questionsPerPage);

  return (
    <div>
      {message && (
        <div style={{
          padding: "6px 12px", borderRadius: 4, marginBottom: 12,
          background: message.includes("Failed") ? "#f8d7da" : "#d4edda",
          color: message.includes("Failed") ? "#721c24" : "#155724",
        }}>
          {message}
        </div>
      )}

      <div style={{ marginBottom: 12, display: "flex", gap: 8 }}>
        <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Answer Keys"}
        </Button>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
        {Array.from({ length: pages }, (_, pageIdx) => {
          const startQ = pageIdx * questionsPerPage;
          const endQ = Math.min(startQ + questionsPerPage, totalQuestions);

          return (
            <table key={pageIdx} className="table table-bordered table-sm" style={{ width: "auto", fontSize: "0.75rem" }}>
              <thead>
                <tr style={{ background: "var(--surface-page)" }}>
                  <th style={{ width: 40 }}>Q</th>
                  {setLabels.map((label) => (
                    <th key={label} style={{ width: 45, textAlign: "center" }}>Set {label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: endQ - startQ }, (_, ri) => {
                  const qIdx = startQ + ri;
                  const qNum = qIdx + 1;
                  return (
                    <tr key={qNum}>
                      <td style={{ fontWeight: "bold", textAlign: "center" }}>{qNum}</td>
                      {setLabels.map((label) => (
                        <td key={label} style={{ padding: 1 }}>
                          <select
                            className="form-control form-control-sm"
                            style={{ fontSize: "0.75rem", padding: "1px 4px", height: 24 }}
                            value={keys[label]?.[qIdx] || 0}
                            onChange={(e) => handleCellChange(label, qIdx, Number(e.target.value))}
                          >
                            <option value={0}>—</option>
                            <option value={1}>1</option>
                            <option value={2}>2</option>
                            <option value={3}>3</option>
                            <option value={4}>4</option>
                          </select>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          );
        })}
      </div>
    </div>
  );
};

export default AnswerKeyTab;
